import React from 'react';
import {StorageKey} from "../../../enums/StorageKey";
import {Plural, t, Trans} from "@lingui/macro";
import AmountTd from "../../Table/Common/AmountTd";
import dayjs from 'dayjs';
import {Col, Tag, Row} from "antd";
import TransactionType from "../../TransactionType";
import TransactionStatus from "../../TransactionStatus";
import InstructionListMenu from "../../ListMenu/Instruction";
import BukkuTooltip from '../../Tooltip';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getUnprocessableFields } from '../../../containers/AppPage/ControlPanel/Instructions/components/InstructionUnprocessableAlert';

function getColumns() {
    return [
        {
            title: t`Name`,
            dataIndex: 'name',
            key: 'name',
            width: 120,
            render: (text, record) => {
                const unprocessableFields = getUnprocessableFields({
                    can_process: record.can_process,
                    group_name: record.group_name,
                });
                return (
                    <div className="center-vertically">
                        <span className="ellipsis-div" style={{lineHeight: '16px'}}>
                            <BukkuTooltip placement="topLeft">{text}</BukkuTooltip>
                        </span>
                        {unprocessableFields.length > 0 && (
                            <BukkuTooltip
                                title={
                                    <Row gutter={8} direction="vertical">
                                        <Col span={24}>
                                            <strong>
                                                <Trans>Cannot process instruction</Trans>
                                            </strong>
                                            <hr style={{
                                                margin: '4px 0',
                                                borderWidth: '0.1px',
                                            }}
                                            />
                                        </Col>
                                        <Col>
                                            <Trans>
                                                <strong>
                                                    <Trans>Contact Group</Trans>
                                                    {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                                                </strong>&nbsp;
                                                feature is required for the following fields:
                                            </Trans>
                                            <ul className="ul__disc" style={{ marginBottom: 6 }}>
                                                {unprocessableFields}
                                            </ul>
                                        </Col>
                                        <Col>
                                            <Trans>Please upgrade your plan or contact support.</Trans>
                                        </Col>
                                    </Row>
                                }
                            >
                                <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 8 }} />
                            </BukkuTooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: t`Period`,
            key: 'period',
            width: 180,
            render: (text, record) => {
                const startDateLocale = dayjs(record.date_from).format('DD/MM/YYYY');
                let endDateLocale = t`Forever`;
                if (record.date_to !== null)
                    endDateLocale = dayjs(record.date_to).format('DD/MM/YYYY');
                return (
                    <span>
                        {startDateLocale}
                        &nbsp;
                        -
                        &nbsp;
                        {endDateLocale}
                    </span>
                );
            },
        },
        {
            title: t`Frequency`,
            key: 'frequency',
            width: 180,
            render: (text, record) => {
                let tagText;
                switch (record.frequency) {
                    case 'year':
                        tagText = t`Every ${record.every} year(s) on ${record.day}/${record.month}`;
                        break;
                    case 'month':
                        tagText = t`Every ${record.every} month(s) on day ${record.day}`;
                        break;
                    case 'week': {
                        let weekDay;
                        switch (record.day) {
                            case 1:
                                weekDay = t`Mon`;
                                break;
                            case 2:
                                weekDay = t`Tue`;
                                break;
                            case 3:
                                weekDay = t`Wed`;
                                break;
                            case 4:
                                weekDay = t`Thu`;
                                break;
                            case 5:
                                weekDay = t`Fri`;
                                break;
                            case 6:
                                weekDay = t`Sat`;
                                break;
                            case 7:
                                weekDay = t`Sun`;
                                break;
                            default:
                                break;
                        }
                        tagText = t`Every ${record.every} week(s) on ${weekDay}`;
                        break;
                    }
                    case 'day':
                        tagText = t`Every ${record.every} day(s)`;
                        break;
                    default: break;
                }
                return <Tag>{ tagText }</Tag>;
            },
        },
        {
            title: t`Next Date`,
            key: 'next_date',
            dataIndex: 'date_next',
            width: 96,
            render: (text, record) => {
                let nextDateLocale = t`Ended`;
                if (record.date_next !== null)
                    nextDateLocale = dayjs(record.date_next).format('DD/MM/YYYY');

                return <span>{ nextDateLocale }</span>;
            },
        },
        {
            title: t`Amount`,
            key: 'amount',
            dataIndex: 'amount',
            width: 135,
            ellipsis: true,
            align: 'right',
            render: (text, record) => <AmountTd currency_code={record.currency_code} amount={text} />,
        },
        {
            title: t`Type`,
            key: 'type',
            dataIndex: 'transaction_type',
            width: 110,
            render: (text, record) => <TransactionType type={text} record={record} isShort />,
        },
        {
            title: t`Create As`,
            key: 'transaction_status',
            dataIndex: 'transaction_status',
            width: 110,
            render: (text, record) => <TransactionStatus status={text} record={record} />,
        },
        {
            title: t`Action`,
            key: 'action',
            width: 130,
            render: (text, record) => <InstructionListMenu record={record} />,
        },
    ];
}

export default {
    storageKey: StorageKey.instructions,
    sortable: [
        'name',
        'next_date',
        'amount',
    ],
    required: [
        'name',
        'action',
    ],
    defaulto: [
        'period',
        'frequency',
        'next_date',
        'amount',
        'type',
    ],
    getColumns,
};
